import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import { getJourneyData } from '../../api/journeyService';
import { journeyActions } from '../../store/journeySlice';
import { getEffectiveApplicationNumber } from '../utils/journeyApplicationNumber';
import { setStoredApplicationNumber } from '../utils/termJourneyStorage';
import {
  getLeadEmail,
  getLeadMobile,
  LEAD_FIELD_EMAIL,
  LEAD_FIELD_MOBILE,
  mergeCumulativeStagesOrdered,
} from '../utils/journeyLeadFields';

function parseStagesFromJourney(journey) {
  const raw =
    journey?.stages ??
    journey?.cummulativeJourneyData ??
    journey?.c_cummulativeJourneyData;
  if (raw == null) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * On Proposal / Proposal Review load: getJourneyData + merge leadFormData.
 * Re-syncs plain applicationNumber from journey API (same as Lead resume).
 */
export function useProposalLeadHydration(applicationNumber, persistRehydrated) {
  const store = useStore();
  const inFlight = useRef(false);
  const completedForApp = useRef(null);

  useEffect(() => {
    const plainAppNumber = getEffectiveApplicationNumber(applicationNumber);
    if (!plainAppNumber || persistRehydrated === false) return;
    if (completedForApp.current === plainAppNumber) return;
    if (inFlight.current) return;

    inFlight.current = true;
    let cancelled = false;

    (async () => {
      try {
        const journey = await getJourneyData(plainAppNumber);
        if (cancelled) return;
        store.dispatch(journeyActions.setJourneyData(journey));
        setStoredApplicationNumber(plainAppNumber);

        const stages = parseStagesFromJourney(journey);
        const merged = stages ? mergeCumulativeStagesOrdered(stages) : {};
        const state = store.getState();
        const current = state.journey.leadFormData || {};

        const next = { ...current };
        let changed = false;

        if (!getLeadEmail(next) && getLeadEmail(merged)) {
          next[LEAD_FIELD_EMAIL] = getLeadEmail(merged);
          changed = true;
        }
        if (!getLeadMobile(next) && getLeadMobile(merged)) {
          next[LEAD_FIELD_MOBILE] = getLeadMobile(merged);
          changed = true;
        }
        const hasDob =
          String(next.dateOfBirth || next.lifeAssuredDob || '').trim() !== '';
        if (!hasDob) {
          const d =
            merged.dateOfBirth ||
            merged.lifeAssuredDob ||
            merged.leadInput?.lifeAssuredDob ||
            merged.leadInput?.dateOfBirth;
          if (d) {
            next.dateOfBirth = d;
            changed = true;
          }
        }
        if (!String(next.gender || '').trim() && merged.gender) {
          next.gender = merged.gender;
          changed = true;
        }
        if (!String(next.fullName || '').trim() && merged.fullName) {
          next.fullName = merged.fullName;
          changed = true;
        }

        if (changed) {
          store.dispatch(journeyActions.setLeadFormData(next));
        }
      } catch {
        /* non-blocking */
      } finally {
        inFlight.current = false;
        if (!cancelled) {
          completedForApp.current = plainAppNumber;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applicationNumber, persistRehydrated, store]);
}
